
import { ICOSFormCases } from "@/models/COS";
import { SMCases, SMUser } from "@/store";
import { validateDate } from "@/utils/dates";
import { TInputRule } from "@nationbetter/nb-components/src/models/Input";
import { computed, defineComponent, getCurrentInstance, onMounted, ref, watch } from "vue-demi";

const DEFAULT_FORM: ICOSFormCases = {
  cos_tier: "",
  cos_sponsor_licence_number: "",
  cos_number: "",
  cos_issue_date: "",
  cos_expiry_date: "",
  cos_note: "",
  sponsor_note: "",
  confirm_creative_contribution: "",
  place_of_birth: "",
  country_of_birth: "",
  gender: "",
  national_insurance_number: "",
  employee_number: "",
  cos_work_start_date: "",
  cos_work_end_date: "",
  hours_work: "",
  job_title: "",
  job_type: "",
  job_description: "",
  if_new_entrant: "",
  basic_salary: "",
  salary_and_allowances: "",
  migrant_salary_for_each: "",
  allowances: "",
  job_title_shortage_list: false,
  maintenance_status: false,
  atas_required: false,
  if_client_contract: false,
  client_contract_details: "",
  phd_required_for_post: false,
  work_address: {
    address: "",
    address2: "",
    city: "",
    county: "",
    postcode: "",
  },
  other_work_addresses: [],
};

export default defineComponent({
  name: "FormCOSCases",

  props: {
    loading: { type: Boolean, default: false },
    details: { type: Object as () => ICOSFormCases, required: true },
  },

  emits: ["update"],

  setup(props, { emit }) {
    const instance = getCurrentInstance()?.proxy;

    const form = ref<ICOSFormCases>({ ...DEFAULT_FORM });

    const isCompliance = computed(() => SMUser.isEmployer && SMUser.isSponsor);

    const isTemporaryVisa = computed(
      () =>
        SMCases.serviceData?.case_template?.name === "Inside Temporary Work Visa" ||
        SMCases.serviceData?.case_template?.name_dev === "Inside Temporary Work Visa"
    );

    const requiredRules: TInputRule[] = [(v) => !!v || String(instance?.$t("errors.noValue"))];

    const options = [
      { value: true, label: "Yes" },
      { value: false, label: "No" },
    ];

    const optionsYear = ["Contract", "Day", "Hour", "Week", "Month", "Year"].map((label) => ({
      value: label,
      label,
    }));

    const getOtherWorkAddressesHeader = (index: number, items: any[]) => {
      const base =
        instance?.$t("labels.otherWorkAddresses") ?? "Other regular work addresses in the UK";
      if (!items.length || items.length === 1) return base;
      return `${base} [${index + 1}]`;
    };

    const setCosDate = (date: string): string => {
      return date && validateDate(date) ? date : "";
    };

    const toBoolean = (value: boolean | string | undefined): boolean => {
      if (typeof value === "boolean") return value;
      if (typeof value === "string") return value === "true";
      return false;
    };

    const setCosFormCases = (data: ICOSFormCases) => {
      const sponsorNote = (data as any).sponsor_note as string | undefined;
      const confirmCreativeContribution = (data as any).confirm_creative_contribution as
        | string
        | undefined;
      const otherWorkAddresses = Array.isArray((data as any).other_work_addresses)
        ? ((data as any).other_work_addresses as ICOSFormCases["other_work_addresses"])
        : [];

      const normalizeIfNewEntrant = (value: unknown) => {
        if (typeof value === "boolean") return value;
        if (typeof value === "string") {
          if (value === "true") return true;
          if (value === "false") return false;
          return value;
        }
        return value ?? "";
      };

      Object.assign(form.value, {
        ...data,
        cos_issue_date: setCosDate(data.cos_issue_date),
        cos_expiry_date: setCosDate(data.cos_expiry_date),
        cos_work_start_date: setCosDate(data.cos_work_start_date),
        cos_work_end_date: setCosDate(data.cos_work_end_date),
        cos_sponsor_licence_number: data.cos_sponsor_licence_number ?? data.sponsor_licence_number,
        sponsor_licence_number: data.sponsor_licence_number ?? data.cos_sponsor_licence_number,
        cos_gender: data.cos_gender || data.gender,
        gender: data.cos_gender || data.gender,
        sponsor_note: sponsorNote ?? data.cos_note ?? "",
        cos_note: data.cos_note || sponsorNote || "",
        job_title_shortage_list: toBoolean(data.job_title_shortage_list as boolean | string),
        maintenance_status: toBoolean(data.maintenance_status as boolean | string),
        if_new_entrant: normalizeIfNewEntrant(data.if_new_entrant) as any,
        atas_required: toBoolean(data.atas_required as boolean | string),
        if_client_contract: toBoolean(data.if_client_contract as boolean | string),
        phd_required_for_post: toBoolean(data.phd_required_for_post as boolean | string),
        other_work_addresses: otherWorkAddresses || [],
        confirm_creative_contribution: confirmCreativeContribution ?? "",
        work_address: {
          address: data.work_address?.address || "",
          address2: data.work_address?.address2 || "",
          city: data.work_address?.city || "",
          county: data.work_address?.county || "",
          postcode: data.work_address?.postcode || "",
        },
        job_type: data.job_type ? data.job_type.replace(/\n/g, " ").trim() : "",
        migrant_salary_for_each:
          optionsYear.find((option) => option.value === data.migrant_salary_for_each)?.value || "",
      });
    };

    const isFormValid = computed(() => {
      const { sponsor_licence_number, cos_sponsor_licence_number } = form.value;

      return isCompliance.value ? !!sponsor_licence_number : !!cos_sponsor_licence_number;
    });

    const dropdownKey = ref(0);

    onMounted(() => {
      setCosFormCases(props.details);

      emit("update", {
        form: form.value,
        valid: isFormValid.value,
      });

      dropdownKey.value++;
    });

    watch(
      () => form.value,
      () => {
        emit("update", {
          form: form.value,
          valid: isFormValid.value,
        });
      },
      { deep: true, immediate: true }
    );

    return {
      form,
      isCompliance,
      isTemporaryVisa,
      requiredRules,
      options,
      optionsYear,
      getOtherWorkAddressesHeader,
      dropdownKey,
    };
  },
});
